import {
  Table,
  Input,
  InputNumber,
  Button,
  Space,
  Popconfirm,
  Typography,
  theme,
} from "antd";
import { DeleteOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { BookingState } from "../../../contexts/BookingWizardContext";
import { isMissingField } from "../utils/mapAnalysisToBooking";
import { SavedProductPicker } from "./SavedProductPicker";
import { BookingProductBulkImportModal } from "../../BookingWizard/BookingProductBulkImportModal";
import type { ImportedBookingProduct } from "../../../utils/bookingProductBulkImport";

const { Text } = Typography;

type Product = NonNullable<BookingState["products"]>[number];

interface ProductEditorTableProps {
  products: Product[];
  missingFields: string[];
  onChange: (products: Product[]) => void;
  onAddProduct?: (product: Product) => void;
  side?: "quest" | "unlocked" | "all";
}

const productHasMissing = (missingFields: string[], index: number): boolean =>
  ["hs_code", "gross_weight", "cbm", "value"].some((field) =>
    isMissingField(missingFields, `products.${index}.${field}`)
  );

const columnHasMissing = (missingFields: string[], field: string): boolean =>
  missingFields.some((key) => key.endsWith(`.${field}`));

export const ProductEditorTable = ({
  products,
  missingFields,
  onChange,
  onAddProduct,
  side = "all",
}: ProductEditorTableProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const visibleIndices = products
    .map((_, index) => index)
    .filter((index) => {
      if (side === "quest") return productHasMissing(missingFields, index);
      if (side === "unlocked") return !productHasMissing(missingFields, index);
      return true;
    });

  const updateProduct = (index: number, patch: Partial<Product>) => {
    const next = products.map((p, i) => (i === index ? { ...p, ...patch } : p));
    onChange(next);
  };

  const removeProduct = (index: number) => {
    onChange(products.filter((_, i) => i !== index));
  };

  const addEmptyProduct = () => {
    onChange([
      ...products,
      {
        name: "",
        hs_code: null,
        gross_weight: null,
        cbm: null,
        value: null,
        pieces: 1,
        cartons: 1,
        ean: null,
        isNew: true,
      },
    ]);
  };

  const handleBulkImport = (imported: ImportedBookingProduct[]) => {
    onChange([...products, ...imported]);
    setBulkImportOpen(false);
  };

  const fieldStatus = (index: number, field: string) =>
    isMissingField(missingFields, `products.${index}.${field}`)
      ? ("warning" as const)
      : undefined;

  const missingColumnTitle = (label: string, field: string) => {
    const missing = columnHasMissing(missingFields, field);
    return (
      <Text style={{ color: missing && side !== "unlocked" ? token.colorWarning : undefined }}>
        {label}
        {missing && side !== "unlocked" ? " *" : ""}
      </Text>
    );
  };

  if (visibleIndices.length === 0 && side !== "all") return null;

  const compact = side === "unlocked";
  const showAll = side === "all";
  const showAddSection = side !== "unlocked" && onAddProduct != null;

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      {products.length === 0 && showAll && (
        <Text type="secondary">{t("bookingWizardV2.details.productsEmptyHint")}</Text>
      )}

      {visibleIndices.length > 0 && (
        <Table
          dataSource={visibleIndices.map((index) => ({
            ...products[index],
            key: index,
            index,
          }))}
          pagination={false}
          size="small"
          tableLayout="fixed"
          style={{ width: "100%" }}
          onRow={(record) => ({
            style: {
              background:
                (side === "quest" || showAll) &&
                productHasMissing(missingFields, record.index)
                  ? token.colorWarningBg
                  : side === "unlocked"
                    ? token.colorSuccessBg
                    : undefined,
            },
          })}
          columns={[
            {
              title: t("products.name"),
              ellipsis: true,
              render: (_, record) => (
                <Input
                  value={products[record.index]?.name}
                  onChange={(e) =>
                    updateProduct(record.index, { name: e.target.value })
                  }
                />
              ),
            },
            {
              title: missingColumnTitle(t("products.hsCode"), "hs_code"),
              width: 88,
              render: (_, record) => (
                <Input
                  value={products[record.index]?.hs_code ?? ""}
                  status={fieldStatus(record.index, "hs_code")}
                  onChange={(e) =>
                    updateProduct(record.index, { hs_code: e.target.value || null })
                  }
                />
              ),
            },
            ...(compact
              ? []
              : [
                  {
                    title: t("products.pieces"),
                    width: 72,
                    render: (_: unknown, record: { index: number }) => (
                      <InputNumber
                        min={1}
                        value={products[record.index]?.pieces ?? 1}
                        onChange={(v) =>
                          updateProduct(record.index, { pieces: v ?? 1 })
                        }
                        style={{ width: "100%" }}
                      />
                    ),
                  },
                  {
                    title: t("products.cartons"),
                    width: 72,
                    render: (_: unknown, record: { index: number }) => (
                      <InputNumber
                        min={1}
                        value={products[record.index]?.cartons ?? 1}
                        onChange={(v) =>
                          updateProduct(record.index, { cartons: v ?? 1 })
                        }
                        style={{ width: "100%" }}
                      />
                    ),
                  },
                ]),
            {
              title: missingColumnTitle(
                `${t("products.grossWeight")} (kg)`,
                "gross_weight"
              ),
              width: 88,
              render: (_, record) => (
                <InputNumber
                  min={0.1}
                  step={0.1}
                  value={products[record.index]?.gross_weight ?? undefined}
                  status={fieldStatus(record.index, "gross_weight")}
                  onChange={(v) => updateProduct(record.index, { gross_weight: v })}
                  style={{ width: "100%" }}
                />
              ),
            },
            {
              title: missingColumnTitle(t("products.cbm"), "cbm"),
              width: 80,
              render: (_, record) => (
                <InputNumber
                  min={0.001}
                  step={0.001}
                  value={products[record.index]?.cbm ?? undefined}
                  status={fieldStatus(record.index, "cbm")}
                  onChange={(v) => updateProduct(record.index, { cbm: v })}
                  style={{ width: "100%" }}
                />
              ),
            },
            {
              title: missingColumnTitle(`${t("products.value")} (USD)`, "value"),
              width: 88,
              render: (_, record) => (
                <InputNumber
                  min={0}
                  value={products[record.index]?.value ?? undefined}
                  status={fieldStatus(record.index, "value")}
                  onChange={(v) => updateProduct(record.index, { value: v })}
                  style={{ width: "100%" }}
                />
              ),
            },
            ...(compact
              ? []
              : [
                  {
                    title: t("products.ean"),
                    width: 100,
                    render: (_: unknown, record: { index: number }) => (
                      <InputNumber
                        stringMode
                        value={products[record.index]?.ean ?? undefined}
                        onChange={(v) =>
                          updateProduct(record.index, {
                            ean: v != null ? Number(v) : null,
                          })
                        }
                        style={{ width: "100%" }}
                      />
                    ),
                  },
                ]),
            {
              title: "",
              width: 52,
              render: (_, record) => (
                <Popconfirm
                  title={t("bookingWizardV2.details.removeProduct")}
                  okText={t("common.delete")}
                  cancelText={t("common.cancel")}
                  onConfirm={() => removeProduct(record.index)}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={t("bookingWizardV2.details.removeProduct")}
                  />
                </Popconfirm>
              ),
            },
          ]}
        />
      )}

      {showAddSection && (
        <div
          style={{
            padding: 12,
            borderRadius: 8,
            border: `1px dashed ${token.colorBorderSecondary}`,
            background: token.colorFillAlter,
          }}
        >
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            {t("bookingWizardV2.details.addProductSection")}
          </Text>
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            <SavedProductPicker
              products={products}
              onAdd={onAddProduct}
              compact
            />
            <Button
              type="dashed"
              icon={<UploadOutlined />}
              onClick={() => setBulkImportOpen(true)}
              block
            >
              {t("bookingWizard.productBulkImport.openButton")}
            </Button>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addEmptyProduct}
              block
            >
              {t("bookingWizardV2.details.addProductManually")}
            </Button>
          </Space>
        </div>
      )}

      <BookingProductBulkImportModal
        open={bulkImportOpen}
        onCancel={() => setBulkImportOpen(false)}
        onImport={handleBulkImport}
      />
    </Space>
  );
};
